import { Injectable } from '@angular/core';
import { BaseList } from '../base-list';
import { ListErrorHandlerService } from './list-error-handler.service';

const DB_MAX_COUNT = 40;

export type ScrollingListLoader<T> = (
  search: ScrollingListSearch,
  cursor?: string | null,
) => Promise<{
  items?: T[];
  cursor?: string | null;
  nextPageToken?: string | null;
}>;

export interface ScrollingListSearch {
  sortBy?: string;
  count?: number;
  reverse?: boolean;
  name?: string;
  selectAll?: boolean;
  skipFirstLoad?: boolean;
  onListUpdate?: (newItems?: unknown[]) => void;
  [key: string]: unknown;
}

export interface ScrollingListItems<T = any> {
  list: T[];
  maxCount: number;
  cursor: any;
  endOfList: boolean;
  add: (items: T[], cursor: any) => void;
  clear: () => void;
  concat: (items: T[]) => void;
}

export interface ScrollingListInstance<T = any> {
  items: ScrollingListItems<T>;
  search: ScrollingListSearch;
  batchOperations: Record<string, unknown>;
  loadingItems: boolean;
  errorMessage: string;
  apiError: string;
  errorCodes: Array<string | number>;
  load: () => void;
  doSearch: () => void;
  sortBy: (cat: string, reverse?: boolean) => void;
  getSelected: () => T[];
  select: (item: T & { selected?: boolean }) => void;
  selectAll: () => void;
  deselectAll: () => void;
}

export interface ScrollingListOperations {
  selectAll?: () => void;
  deselectAll?: () => void;
  [key: string]: unknown;
}

/**
 * Angular-native factory matching the legacy
 * `ScrollingListService(listService, search)` call shape used by simple /
 * non-page lists (billing, selectors, settings, editor, schedules, template). Prefer
 * `PaginatedScrollingListService` + `PageWithListComponent` for full page lists.
 */
@Injectable({
  providedIn: 'root',
})
export class ScrollingListFactoryService {
  constructor(private readonly listErrorHandler: ListErrorHandlerService) {}

  create<T>(
    listService: ScrollingListLoader<T>,
    search?: ScrollingListSearch,
    listOperations?: ScrollingListOperations,
  ): ScrollingListInstance<T> {
    // Keep the caller search object reference (legacy _.defaults behavior) so
    // components can mutate shared fields (folderId, includeSubcompanies, …).
    const searchObject: ScrollingListSearch = search || {};
    if (searchObject.sortBy === undefined) {
      searchObject.sortBy = 'name';
    }
    if (searchObject.count === undefined) {
      searchObject.count = DB_MAX_COUNT;
    }
    if (searchObject.reverse === undefined) {
      searchObject.reverse = false;
    }
    if (searchObject.name === undefined) {
      searchObject.name = 'Items';
    }
    if (searchObject.selectAll === undefined) {
      searchObject.selectAll = false;
    }

    const factory: ScrollingListInstance<T> = {
      items: new BaseList(DB_MAX_COUNT) as ScrollingListItems<T>,
      batchOperations: {},
      search: searchObject,
      loadingItems: false,
      errorMessage: '',
      apiError: '',
      errorCodes: [],
      load: () => undefined,
      doSearch: () => undefined,
      sortBy: () => undefined,
      getSelected: () => [],
      select: () => undefined,
      selectAll: () => undefined,
      deselectAll: () => undefined,
    };

    const clearMessages = () => {
      factory.errorMessage = '';
      factory.apiError = '';
      factory.errorCodes = [];
    };

    const clearList = () => {
      factory.items.clear();
      factory.search.selectAll = false;
    };

    factory.load = () => {
      clearMessages();

      if (
        (!factory.items.list.length ||
          (!factory.items.endOfList && factory.items.cursor)) &&
        !factory.loadingItems
      ) {
        factory.loadingItems = true;
        let newItems: T[] | undefined;

        listService(factory.search, factory.items.cursor)
          .then((result) => {
            newItems = result.items;
            factory.items.add(
              result.items ? result.items : [],
              result.cursor || result.nextPageToken,
            );
            factory.search.selectAll = false;
          })
          .catch((e) => {
            factory.errorMessage =
              'Failed to load ' + factory.search.name + '.';
            factory.apiError = this.listErrorHandler.processErrorCode(
              factory.search.name,
              'load',
              e,
            );
            console.error(factory.errorMessage, e);
          })
          .finally(() => {
            factory.loadingItems = false;
            if (factory.search.onListUpdate) {
              factory.search.onListUpdate(newItems);
            }
          });
      }
    };

    factory.sortBy = (cat: string, reverse?: boolean) => {
      clearList();

      if (cat !== factory.search.sortBy) {
        factory.search.sortBy = cat;
        factory.search.reverse = !!reverse;
      } else {
        factory.search.reverse = !factory.search.reverse;
      }

      factory.load();
    };

    factory.doSearch = () => {
      clearList();
      factory.load();
    };

    factory.getSelected = () => {
      return factory.items.list.filter(
        (item) => (item as T & { selected?: boolean }).selected,
      );
    };

    factory.select = (item: T & { selected?: boolean }) => {
      if (!item) {
        return;
      }

      item.selected = !item.selected;
      factory.search.selectAll = allSelected(factory.items.list);
    };

    factory.selectAll = () => {
      if (!factory.items.list.length) {
        return;
      }

      factory.search.selectAll = !factory.search.selectAll;

      if (listOperations?.selectAll) {
        listOperations.selectAll();
      } else {
        factory.items.list.forEach((item) => {
          (item as T & { selected?: boolean }).selected =
            factory.search.selectAll;
        });
      }
    };

    factory.deselectAll = () => {
      if (!factory.items.list.length) {
        return;
      }

      factory.search.selectAll = false;

      if (listOperations?.deselectAll) {
        listOperations.deselectAll();
      } else {
        factory.items.list.forEach((item) => {
          (item as T & { selected?: boolean }).selected = false;
        });
      }
    };

    if (!factory.search.skipFirstLoad) {
      factory.load();
    }

    return factory;
  }
}

function allSelected(list: unknown[]): boolean {
  return (list as { selected?: boolean }[]).every((item) => item.selected);
}
